import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, Clock, Tag, ArrowRight, Search, ChevronRight } from 'lucide-react';
import { SEO } from '../../shared/components/SEO';
import { blogPosts, type BlogPost } from '../../shared/data/blogData';

const categories = [
  'All Articles',
  'Cattle Nutrition',
  'Swine Farming',
  'Edible Oils',
  'Quality & Safety',
  'Farm Management'
] as const;

export function Blog() {
  const [selectedCategory, setSelectedCategory] = useState<string>('All Articles');
  const [searchQuery, setSearchQuery] = useState('');

  const filteredPosts = useMemo(() => {
    return blogPosts.filter((post) => {
      const matchesCategory = selectedCategory === 'All Articles' || post.category === selectedCategory;
      const query = searchQuery.toLowerCase().trim();
      const matchesSearch =
        !query ||
        post.title.toLowerCase().includes(query) ||
        post.summary.toLowerCase().includes(query) ||
        post.keywords.some((kw) => kw.toLowerCase().includes(query));
      return matchesCategory && matchesSearch;
    });
  }, [selectedCategory, searchQuery]);

  return (
    <div className="min-h-screen bg-page">
      <SEO
        title="Agritech Knowledge Hub & Farm Guides | ZFAST Agritech India"
        description="Comprehensive guides on dairy cow nutrition, commercial pig farming FCR, Kachi Ghani mustard oil purity, aflatoxin prevention, and livestock feed calculators."
        path="/blog"
        breadcrumbs={[{ label: 'Knowledge Hub', href: '/blog' }]}
      />

      {/* Hero Header */}
      <section className="relative overflow-hidden bg-forest py-16 text-white md:py-20">
        <div className="absolute inset-0 opacity-10 bg-[radial-gradient(#ffffff_1px,transparent_1px)] [background-size:16px_16px]" />
        <div className="relative mx-auto max-w-[1540px] px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-tint">
            <Link to="/" className="hover:underline">Home</Link>
            <ChevronRight className="h-3.5 w-3.5" />
            <span>Knowledge Hub</span>
          </div>

          <div className="mt-4 max-w-3xl">
            <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-bold uppercase tracking-widest text-[#DDEFCB] backdrop-blur-sm">
              <BookOpen className="h-3.5 w-3.5" />
              Agritech Insights & Farm Guides
            </div>
            <h1 className="mt-4 text-3xl font-black tracking-tight text-white sm:text-4xl lg:text-5xl">
              Agritech Knowledge Hub & Technical Guides
            </h1>
            <p className="mt-4 text-base leading-relaxed text-white/85 sm:text-lg">
              Explore scientifically grounded articles on dairy cattle feed formulation, commercial swine stage nutrition, edible oil quality standards, and farm productivity best practices.
            </p>
          </div>

          {/* Search Bar */}
          <div className="mt-8 max-w-xl">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-charcoal/50" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search articles by keyword (e.g. cattle feed, FCR, mustard oil, aflatoxin)..."
                className="w-full rounded-2xl border-0 bg-white py-3.5 pl-12 pr-4 text-sm font-semibold text-charcoal shadow-lg ring-1 ring-black/5 placeholder:text-charcoal/45 focus:outline-none focus:ring-2 focus:ring-[#7CB342]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Category Tabs & Articles Section */}
      <section className="mx-auto max-w-[1540px] px-4 py-12 sm:px-6 lg:px-8">
        {/* Category Filter Pills */}
        <div className="flex flex-wrap items-center gap-2 pb-8 border-b border-forest/10">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setSelectedCategory(cat)}
              className={[
                'rounded-full px-4 py-2 text-xs font-bold transition-all sm:text-sm',
                selectedCategory === cat
                  ? 'bg-forest text-white shadow-md'
                  : 'bg-white text-ink/70 hover:bg-forest/10 hover:text-forest'
              ].join(' ')}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Article Grid */}
        <div className="mt-10">
          {filteredPosts.length === 0 ? (
            <div className="rounded-3xl border border-forest/10 bg-white p-12 text-center shadow-soft">
              <p className="text-base font-bold text-ink/70">No articles found matching your criteria.</p>
              <button
                onClick={() => { setSelectedCategory('All Articles'); setSearchQuery(''); }}
                className="mt-4 inline-flex items-center gap-2 rounded-full bg-forest px-5 py-2.5 text-xs font-bold text-white hover:bg-forest/90"
              >
                Reset Filters
              </button>
            </div>
          ) : (
            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
              {filteredPosts.map((post) => (
                <article
                  key={post.slug}
                  className="group flex flex-col overflow-hidden rounded-3xl border border-forest/10 bg-white shadow-soft transition-all duration-300 hover:-translate-y-1 hover:shadow-hover"
                >
                  <Link to={`/blog/${post.slug}`} className="block relative aspect-[16/10] overflow-hidden bg-forest/5">
                    <img
                      src={post.coverImage}
                      alt={post.title}
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute left-4 top-4 rounded-full bg-forest/90 px-3 py-1 text-[11px] font-bold tracking-wide text-white backdrop-blur-sm">
                      {post.category}
                    </div>
                  </Link>

                  <div className="flex flex-1 flex-col p-6 sm:p-7">
                    <div className="flex items-center gap-4 text-xs font-medium text-ink/50">
                      <span className="flex items-center gap-1.5">
                        <Clock className="h-3.5 w-3.5 text-forest" />
                        {post.readTime}
                      </span>
                      <span>•</span>
                      <span>{new Date(post.publishedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                    </div>

                    <h2 className="mt-3 text-lg font-black leading-snug text-ink group-hover:text-forest transition-colors sm:text-xl">
                      <Link to={`/blog/${post.slug}`}>
                        {post.title}
                      </Link>
                    </h2>

                    <p className="mt-3 line-clamp-3 text-sm leading-relaxed text-ink/70 flex-1">
                      {post.summary}
                    </p>

                    <div className="mt-6 pt-5 border-t border-forest/10 flex items-center justify-between">
                      <div>
                        <p className="text-xs font-bold text-ink">{post.author.name}</p>
                        <p className="text-[11px] text-ink/50">{post.author.role}</p>
                      </div>

                      <Link
                        to={`/blog/${post.slug}`}
                        className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-tint text-forest transition group-hover:bg-forest group-hover:text-white"
                        aria-label={`Read ${post.title}`}
                      >
                        <ArrowRight className="h-4 w-4" />
                      </Link>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>

        {/* Bottom CTA Banner */}
        <div className="mt-16 overflow-hidden rounded-3xl bg-gradient-to-br from-forest via-[#154B31] to-[#0A2A19] p-8 text-white sm:p-12">
          <div className="grid gap-6 lg:grid-cols-2 lg:items-center">
            <div>
              <span className="inline-block rounded-full bg-white/10 px-3.5 py-1 text-xs font-black uppercase tracking-widest text-[#DDEFCB]">
                Partner With ZFAST
              </span>
              <h2 className="mt-3 text-2xl font-black text-white sm:text-3xl">
                Ready to Upgrade Your Farm Nutrition or Become a Regional Dealer?
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-white/80 sm:text-base">
                Connect with our nutrition experts and dealership management team in Jaipur to discuss wholesale supply, custom rations, and distribution territories.
              </p>
            </div>
            <div className="flex flex-wrap gap-4 lg:justify-end">
              <Link
                to="/products"
                className="inline-flex items-center gap-2 rounded-full bg-[#7CB342] px-6 py-3.5 text-sm font-black text-white transition hover:bg-[#689F38] shadow-md"
              >
                Explore Products
                <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/dealer-partnership"
                className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/20 px-6 py-3.5 text-sm font-black text-white transition hover:bg-white/20"
              >
                Apply for Dealership
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
